import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { buildApiUrl } from '../config/api';
import Navbar from '../components/Navbar';
import './YingshanBoard.css';

const DEFAULT_SITE = 'yingshan';
const POLL_MS = 5000;

const RGB_CLASS = {
  green: 'rgb-green',
  yellow: 'rgb-yellow',
  red: 'rgb-red',
  gray: 'rgb-gray',
};

const DEVICE_BADGE = {
  green: '运行',
  yellow: '待机',
  red: '故障',
  gray: '未知',
};

function normalizeRgb(v) {
  const s = String(v || 'gray').toLowerCase();
  return RGB_CLASS[s] ? s : 'gray';
}

function splitZoneName(name) {
  const raw = String(name || '');
  if (raw.includes('/')) {
    const i = raw.indexOf('/');
    return { zone: raw.slice(0, i), name: raw.slice(i + 1) };
  }
  return { zone: '', name: raw };
}

function equipmentGroup(name) {
  const n = String(name || '');
  if (n.includes('泵')) return '泵';
  if (n.includes('流量计') || n.includes('流量仪') || (n.includes('流量') && !n.includes('泵'))) return '计量器';
  if (
    /液位|泥位|pH|PH|DO|ORP|溶解氧|压力|温度|传感器|仪表|检测|视觉/.test(n)
  ) {
    return '检测仪器';
  }
  if (n.includes('阀')) return '阀';
  if (n.includes('风机')) return '风机';
  if (n.includes('搅拌机') || n.includes('推流')) return '搅拌机';
  return '其它设备';
}

const GROUP_ORDER = ['泵', '检测仪器', '计量器', '阀', '搅拌机', '风机', '其它设备', '其它'];

function RingGauge({ label, value, total, color }) {
  const t = Math.max(Number(total) || 1, 1);
  const v = Math.max(0, Number(value) || 0);
  const pct = Math.min(100, Math.round((v / t) * 100));
  const style = {
    background: `conic-gradient(${color} ${pct}%, rgba(30, 200, 255, 0.12) 0)`,
  };
  return (
    <div className="ys-gauge">
      <div className="ys-gauge__ring" style={style}>
        <div className="ys-gauge__hole">
          <div className="ys-gauge__num" style={{ color }}>
            {v}
          </div>
        </div>
      </div>
      <div className="ys-gauge__label">{label}</div>
    </div>
  );
}

function templateAiReply(question, ctx) {
  const text = String(question || '').trim();
  if (/DO|溶解氧|曝气/.test(text)) {
    return `曝气 DO 通常控制在 2~4 mg/L。当前摘要：${ctx || '暂无告警点'}。请结合监视环图与明细列表核对。`;
  }
  if (/芬顿|加药|pH|PH/.test(text)) {
    return `芬顿区关注亚铁/pH、双氧水比例。当前摘要：${ctx || '暂无告警点'}。`;
  }
  if (/排泥|泥位/.test(text)) {
    return `排泥关注泥位阈值（约 3m 提示 / 4m 报警，以现场锁定为准）。当前摘要：${ctx || '暂无告警点'}。`;
  }
  return `已根据网关上报数据作答。您问：${text.slice(0, 80)}。当前异常/注意点：${ctx || '无黄红点'}。可继续追问具体构筑物或设备名。`;
}

function YingshanBoard() {
  const [sites, setSites] = useState([{ site_id: DEFAULT_SITE, name: '营山污水厂' }]);
  const [siteId, setSiteId] = useState(DEFAULT_SITE);
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatLog, setChatLog] = useState([]);
  const [chatBusy, setChatBusy] = useState(false);
  const [aiHealth, setAiHealth] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(buildApiUrl('/hmi/sites'));
        const json = await res.json();
        const items = (json.data && (json.data.items || json.data)) || [];
        if (!cancelled && Array.isArray(items) && items.length) {
          setSites(items);
          const has = items.some((s) => s.site_id === siteId);
          if (!has) setSiteId(items[0].site_id || DEFAULT_SITE);
        }
      } catch (_) {
        /* keep default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchBoard = useCallback(async () => {
    try {
      const res = await fetch(buildApiUrl(`/hmi/board/${encodeURIComponent(siteId)}`));
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error((json && json.message) || `请求失败(${res.status})`);
      }
      setBoard(json.data);
      setError(null);
    } catch (e) {
      setError(e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    setLoading(true);
    fetchBoard();
    const timer = setInterval(fetchBoard, POLL_MS);
    return () => clearInterval(timer);
  }, [fetchBoard]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(buildApiUrl('/hmi/chat/health'));
        const json = await res.json();
        if (!cancelled && json && json.success) setAiHealth(json.data);
      } catch (_) {
        if (!cancelled) setAiHealth({ up: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatLog]);

  const devices = (board && board.devices) || [];
  const sensors = (board && board.sensors) || [];
  const chemicals = (board && board.chemicals) || [];
  const ts = board && (board.timestamp || board.ts);
  const siteLabel = (sites.find((s) => s.site_id === siteId) || {}).name || siteId;

  const counts = useMemo(() => {
    const c = { green: 0, yellow: 0, red: 0, gray: 0 };
    devices.forEach((d) => {
      const rgb = normalizeRgb(d.rgb || d.rgb_status);
      c[rgb] = (c[rgb] || 0) + 1;
    });
    sensors.forEach((s) => {
      const rgb = normalizeRgb(s.rgb || s.rgb_status || 'green');
      c[rgb] = (c[rgb] || 0) + 1;
    });
    return c;
  }, [devices, sensors]);

  const totalPoints = devices.length + sensors.length;

  const hero = useMemo(() => {
    if (selectedId) {
      const d = devices.find((x) => x.id === selectedId);
      if (d) {
        const { zone, name } = splitZoneName(d.name);
        return {
          title: `${zone ? `${zone}/` : ''}${name}`,
          value: d.state || DEVICE_BADGE[normalizeRgb(d.rgb)] || '—',
          sub: '设备',
        };
      }
      const s = sensors.find((x) => x.id === selectedId);
      if (s) {
        const { zone, name } = splitZoneName(s.name);
        return {
          title: `${zone ? `${zone}/` : ''}${name}`,
          value: s.value != null ? Number(s.value).toFixed ? Number(s.value).toFixed(2) : s.value : '—',
          sub: s.unit || '测点',
        };
      }
    }
    const prefer = sensors.find((s) => /液位|DO|pH|ORP|流量/.test(String(s.name || ''))) || sensors[0];
    if (prefer) {
      const { zone, name } = splitZoneName(prefer.name);
      let val = prefer.value;
      try {
        val = Number(prefer.value).toFixed(2);
      } catch (_) {
        /* keep */
      }
      return {
        title: `${zone ? `${zone}/` : ''}${name}`,
        value: val != null ? val : '—',
        sub: prefer.unit || '关键测点',
      };
    }
    return { title: '关键测点', value: '—', sub: '等待网关' };
  }, [selectedId, devices, sensors]);

  const alerts = useMemo(() => {
    const list = [];
    devices.forEach((d) => {
      const rgb = normalizeRgb(d.rgb || d.rgb_status);
      if (rgb === 'red') list.push({ sev: '故障', msg: `${d.name} · ${d.state || '故障'}`, rgb });
      else if (rgb === 'yellow') list.push({ sev: '注意', msg: `${d.name} · ${d.state || '待机'}`, rgb });
    });
    sensors.forEach((s) => {
      const rgb = normalizeRgb(s.rgb || s.rgb_status);
      if (rgb === 'red' || rgb === 'yellow') {
        list.push({
          sev: rgb === 'red' ? '告警' : '提示',
          msg: `${s.name}=${s.value ?? '—'}${s.unit || ''}`,
          rgb,
        });
      }
    });
    return list.slice(0, 40);
  }, [devices, sensors]);

  const groupedDevices = useMemo(() => {
    const map = new Map();
    devices.forEach((d) => {
      const { name } = splitZoneName(d.name);
      const g = equipmentGroup(name);
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(d);
    });
    const keys = [...map.keys()].sort(
      (a, b) => (GROUP_ORDER.indexOf(a) === -1 ? 99 : GROUP_ORDER.indexOf(a)) - (GROUP_ORDER.indexOf(b) === -1 ? 99 : GROUP_ORDER.indexOf(b))
    );
    return keys.map((k) => ({ group: k, items: map.get(k) }));
  }, [devices]);

  const miniSensors = sensors.slice(0, 6);

  const alertContext = useMemo(() => {
    const lines = [];
    devices.forEach((d) => {
      const rgb = normalizeRgb(d.rgb);
      if (rgb === 'red' || rgb === 'yellow') lines.push(`${d.name}[${rgb}]`);
    });
    sensors.forEach((s) => {
      const rgb = normalizeRgb(s.rgb);
      if (rgb === 'red' || rgb === 'yellow') lines.push(`${s.name}=${s.value}[${rgb}]`);
    });
    return lines.slice(0, 12).join('；') || '';
  }, [devices, sensors]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    setChatInput('');
    setChatBusy(true);
    setChatLog((prev) => [
      ...prev,
      { role: 'user', text },
      { role: 'assistant', text: '模型思考中，请稍候…', pending: true },
    ]);

    const history = chatLog
      .filter((m) => !m.pending)
      .slice(-6)
      .map((m) => ({ role: m.role, content: m.text }));

    try {
      const res = await fetch(buildApiUrl('/hmi/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: text,
          context: alertContext,
          history,
        }),
      });
      const json = await res.json();
      const data = (json && json.data) || {};
      let reply = data.reply || '';
      if (!res.ok || !json.success || !reply) {
        reply = templateAiReply(text, alertContext);
      } else if (data.source === 'template' && data.error) {
        reply = `${reply}\n\n（说明：Ollama 暂不可用，已降级模板：${String(data.error).slice(0, 80)}）`;
      }
      const src = data.source === 'ollama' ? 'ollama' : 'template';
      setChatLog((prev) => {
        const next = prev.filter((m) => !m.pending);
        next.push({ role: 'assistant', text: reply, source: src });
        return next;
      });
      if (data.source === 'ollama') {
        setAiHealth((h) => ({ ...(h || {}), up: true, has_model: true }));
      }
    } catch (_) {
      setChatLog((prev) => {
        const next = prev.filter((m) => !m.pending);
        next.push({
          role: 'assistant',
          text: templateAiReply(text, alertContext),
          source: 'template',
        });
        return next;
      });
    } finally {
      setChatBusy(false);
    }
  };

  return (
    <>
      <Navbar />
    <div className="ys-monitor">
      <header className="ys-monitor__header">
        <div className="ys-monitor__titles">
          <h1>实时监控 · 三色状态</h1>
          <p>设备状态总览 · 网关上报 · 网页随时查看</p>
        </div>
        <div className="ys-monitor__meta">
          <label>
            站点{' '}
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              {sites.map((s) => (
                <option key={s.site_id} value={s.site_id}>
                  {s.name || s.site_id}
                </option>
              ))}
            </select>
          </label>
          {board && (
            <span className={`ys-badge ${board.offline ? 'offline' : 'online'}`}>
              {board.offline ? '● 站点离线' : '● 系统运行中'}
            </span>
          )}
          <span className="ys-ts">{ts ? new Date(ts).toLocaleString('zh-CN') : '—'} · {siteLabel}</span>
          <button type="button" className="ys-btn" onClick={fetchBoard}>
            刷新
          </button>
        </div>
      </header>

      {loading && !board && <p className="ys-hint">加载中…</p>}
      {error && <p className="ys-error">{error}</p>}

      <div className="ys-monitor__body">
        <div className="ys-col ys-col--left">
          <section className="ys-panel ys-hero-row">
            <div className="ys-hero">
              <div className="ys-hero__value">{hero.value}</div>
              <div className="ys-hero__name">{hero.title}</div>
              <div className="ys-hero__sub">{hero.sub}</div>
            </div>
            <ul className="ys-mini">
              {miniSensors.length === 0 && <li className="ys-muted">暂无测点</li>}
              {miniSensors.map((s) => {
                const rgb = normalizeRgb(s.rgb || s.rgb_status || 'green');
                return (
                  <li key={s.id} className={RGB_CLASS[rgb]}>
                    <button type="button" onClick={() => setSelectedId(s.id)}>
                      {s.name} {s.value != null ? s.value : '—'}
                      {s.unit ? ` ${s.unit}` : ''}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="ys-panel">
            <h2>设备当前状态</h2>
            <div className="ys-gauges">
              <RingGauge label="正常" value={counts.green} total={totalPoints || 1} color="#5dff9a" />
              <RingGauge label="设备总数" value={totalPoints} total={totalPoints || 1} color="#1ec8ff" />
              <RingGauge label="注意" value={counts.yellow} total={totalPoints || 1} color="#ffd166" />
              <RingGauge label="异常" value={counts.red} total={totalPoints || 1} color="#ff6b8a" />
            </div>
          </section>

          <section className="ys-panel ys-bottom-left">
            <div className="ys-alerts">
              <h2>规则告警</h2>
              <ul>
                {alerts.length === 0 && <li className="ys-muted">暂无黄/红告警</li>}
                {alerts.map((a, i) => (
                  <li key={`${a.msg}-${i}`} className={RGB_CLASS[a.rgb]}>
                    〔{a.sev}〕 {a.msg}
                  </li>
                ))}
              </ul>
            </div>
            <div className="ys-live">
              <h2>实时监控状态</h2>
              <RingGauge label="在线点位" value={totalPoints} total={Math.max(totalPoints, 1)} color="#3de0ff" />
              <p className="ys-muted">{board && board.offline ? '站点离线' : '采样刷新中'}</p>
            </div>
          </section>
        </div>

        <div className="ys-col ys-col--right">
          <section className="ys-panel ys-list-panel">
            <div className="ys-list-head">
              <h2>设备明细列表</h2>
              <span className="ys-muted">共 {devices.length} 项</span>
            </div>
            <div className="ys-device-list">
              {groupedDevices.length === 0 && <p className="ys-muted">暂无设备数据</p>}
              {groupedDevices.map(({ group, items }) => (
                <div key={group} className="ys-group">
                  <div className="ys-group__title">▸ {group}</div>
                  {items.map((d) => {
                    const rgb = normalizeRgb(d.rgb || d.rgb_status);
                    const badge = d.state || DEVICE_BADGE[rgb] || rgb;
                    const active = selectedId === d.id;
                    return (
                      <button
                        type="button"
                        key={d.id}
                        className={`ys-device-row ${RGB_CLASS[rgb]} ${active ? 'active' : ''}`}
                        onClick={() => setSelectedId(d.id)}
                      >
                        <span className={`ys-dot ${RGB_CLASS[rgb]}`} />
                        <span className="ys-device-row__name">{d.name}</span>
                        <span className="ys-device-row__badge">[{badge}]</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <section className="ys-panel ys-sensors-bar">
        <h2>传感器 / 药剂</h2>
        <div className="ys-sensor-grid">
          {sensors.map((s) => {
            const rgb = normalizeRgb(s.rgb || s.rgb_status || 'green');
            return (
              <button
                type="button"
                key={s.id}
                className={`ys-sensor-card ${RGB_CLASS[rgb]}`}
                onClick={() => setSelectedId(s.id)}
              >
                <div className="ys-sensor-card__label">{s.name}</div>
                <div className="ys-sensor-card__value">
                  {s.value != null ? s.value : '—'}
                  {s.unit ? <span>{s.unit}</span> : null}
                </div>
              </button>
            );
          })}
          {chemicals.map((c) => (
            <div key={c.id || c.name} className="ys-sensor-card">
              <div className="ys-sensor-card__label">{c.name}</div>
              <div className="ys-sensor-card__value">
                {c.liters != null ? c.liters : '—'}
                <span>L</span>
              </div>
            </div>
          ))}
          {sensors.length === 0 && chemicals.length === 0 && (
            <p className="ys-muted">暂无传感器/药剂数据</p>
          )}
        </div>
      </section>

      <section className="ys-panel ys-chat">
        <div className="ys-list-head">
          <h2>智能值班对话</h2>
          <span className="ys-muted">
            {aiHealth && aiHealth.up
              ? `Ollama · ${aiHealth.model || 'qwen3:8b'}${aiHealth.has_model === false ? '（缺模型）' : ''}`
              : '网关摘要 · 优先 Ollama，失败则模板'}
          </span>
        </div>
        <div className="ys-chat__log">
          {chatLog.length === 0 && <p className="ys-muted">对话记录将显示在这里…</p>}
          {chatLog.map((m, i) => (
            <div key={i} className={`ys-chat__msg ${m.role}`}>
              <strong>{m.role === 'user' ? '你' : '助手'}：</strong>
              {m.text}
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <div className="ys-chat__input">
          <input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendChat()}
            placeholder="输入问题，例如：当前溶解氧怎么样？"
          />
          <button type="button" className="ys-btn primary" onClick={sendChat} disabled={chatBusy}>
            {chatBusy ? '思考中…' : '发送'}
          </button>
        </div>
      </section>
    </div>
    </>
  );
}

export default YingshanBoard;
